import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LOGO_URL = '/images/gtra-logo.svg';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/public/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (data.code === 0) {
        setSubscribed(true);
        setEmail('');
      } else {
        setError(data.message || '订阅失败');
      }
    } catch {
      setError('网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh' ? 'en' : 'zh');
  };

  const quickLinks = [
    { key: 'nav.about', href: '/#about' },
    { key: 'nav.partners', href: '/partners' },
    { key: 'nav.events', href: '/events' },
    { key: 'nav.guests', href: '/#guests' },
    { key: 'nav.contact', href: '/#contact' },
  ];

  return (
    <footer className="relative bg-[#0A2540] text-white overflow-hidden">
      {/* World Map Decorative BG */}
      <div
        className="absolute inset-0 opacity-5 bg-center bg-no-repeat bg-cover pointer-events-none"
        style={{
          backgroundImage: `url(/images/world-map.png)`,
        }}
      />

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-16 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img src={LOGO_URL} alt="GTRA" className="w-full h-full object-contain" />
                </div>
                <div>
                  <div className="font-bold text-lg">GTRA</div>
                  <div className="text-white/50 text-xs tracking-widest uppercase">
                    {i18n.language === 'zh' ? '全球科创圆桌联盟' : 'Global Tech Roundtable'}
                  </div>
                </div>
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                {t('footer.description')}
              </p>
              {/* Social Links */}
              <div className="flex items-center gap-3">
                {[
                  { icon: 'ri-linkedin-box-fill', label: 'LinkedIn' },
                  { icon: 'ri-twitter-x-line', label: 'X/Twitter' },
                  { icon: 'ri-wechat-fill', label: 'WeChat' },
                  { icon: 'ri-youtube-fill', label: 'YouTube' },
                ].map(({ icon, label }) => (
                  <button
                    key={label}
                    aria-label={label}
                    className="w-8 h-8 flex items-center justify-center rounded-full border border-white/20 text-white/60 hover:text-white hover:border-white/50 hover:bg-white/10 transition-all cursor-pointer"
                  >
                    <i className={`${icon} text-base`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-semibold text-sm tracking-widest uppercase text-white/40 mb-5">
                {t('footer.quickLinks')}
              </h3>
              <ul className="space-y-3">
                {quickLinks.map(({ key, href }) => (
                  <li key={key}>
                    <a
                      href={href}
                      className="text-white/70 hover:text-white text-sm transition-colors cursor-pointer flex items-center gap-2"
                    >
                      <i className="ri-arrow-right-s-line text-[#2C7DA0]" />
                      {t(key)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div id="contact">
              <h3 className="font-semibold text-sm tracking-widest uppercase text-white/40 mb-5">
                {t('footer.contactUs')}
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm text-white/70">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-map-pin-2-line text-[#2C7DA0]" />
                  </div>
                  <span>{t('footer.address')}</span>
                </li>
                <li className="flex items-center gap-3 text-sm text-white/70">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-[#2C7DA0]" />
                  </div>
                  <a href="mailto:info@gtraweb3.com" className="hover:text-white transition-colors">
                    {t('footer.email')}
                  </a>
                </li>
                <li className="flex items-center gap-3 text-sm text-white/70">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-[#2C7DA0]" />
                  </div>
                  <span>{t('footer.phone')}</span>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div>
              <h3 className="font-semibold text-sm tracking-widest uppercase text-white/40 mb-5">
                {t('footer.subscribe.title')}
              </h3>
              <p className="text-white/60 text-sm mb-4">{t('footer.subscribe.desc')}</p>
              {subscribed ? (
                <div className="flex items-center gap-2 text-[#2C7DA0] text-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-lg" />
                  </div>
                  <span>{t('footer.subscribe.success')}</span>
                </div>
              ) : (
                <form
                  id="newsletter-form"
                  onSubmit={handleSubscribe}
                  className="flex flex-col gap-2"
                >
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('footer.subscribe.placeholder')}
                    required
                    className="px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#2C7DA0] transition-colors"
                  />
                  {error && (
                    <p className="text-red-400 text-xs">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2.5 bg-[#2C7DA0] hover:bg-[#2C7DA0]/90 rounded-lg text-sm font-medium text-white transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? t('common.loading') : t('footer.subscribe.btn')}
                  </button>
                </form>
              )}

              {/* Language Toggle */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <button
                  onClick={toggleLang}
                  className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-global-line" />
                  </div>
                  <span>{i18n.language === 'zh' ? 'Switch to English' : '切换为中文'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/40 text-xs">{t('footer.copyright')}</p>
            <div className="flex items-center gap-4 text-xs text-white/40">
              <a href="#" rel="nofollow" className="hover:text-white/70 transition-colors cursor-pointer">
                {i18n.language === 'zh' ? '隐私政策' : 'Privacy Policy'}
              </a>
              <a href="#" rel="nofollow" className="hover:text-white/70 transition-colors cursor-pointer">
                {i18n.language === 'zh' ? '使用条款' : 'Terms of Use'}
              </a>
              <Link
                to="/admin/login"
                className="opacity-0 hover:opacity-30 transition-opacity duration-300 cursor-pointer select-none text-white/20 hover:text-white/50"
                title="Admin"
              >
                <i className="ri-settings-3-line text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
